"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import { clientLogger } from "~/lib/logger-client"

/**
 * Tabbed SKILL.md preview carousel — rotates through several skills
 * so the buyer sees the FRONTMATTER VARIETY rather than a single
 * static `/storyboard` example.
 *
 * Each tab renders the same terminal-window frame with a different
 * SKILL.md body so the buyer can compare:
 *   - context: inline vs fork (different access models)
 *   - model: opus vs sonnet (cost-sensitive routing)
 *   - allowed-tools: which Claude Code tools the skill needs
 *
 * Client component so the tab state stays interactive on the static
 * marketing page. Keeps the rest of SkillsShowcase server-rendered.
 */

interface SkillExample {
  /** Tab label (kept short for compact pill rendering). */
  tabLabel: string
  /** Path shown in the terminal-window chrome. */
  filePath: string
  /** Status pill at the top-right of the chrome (mode · model). */
  badge: string
  /** YAML body lines — rendered as syntax-highlighted text. */
  body: SkillLine[]
}

type SkillLine =
  | { type: "delim" }
  | { type: "kv"; k: string; v: string }
  | { type: "kv-block"; k: string }
  | { type: "indent"; text: string }
  | { type: "blank" }

const SKILLS: SkillExample[] = [
  {
    tabLabel: "/storyboard",
    filePath: ".claude/skills/storyboard/SKILL.md",
    badge: "inline · claude-opus-4-7",
    body: [
      { type: "delim" },
      { type: "kv", k: "name", v: "storyboard" },
      { type: "kv-block", k: "description" },
      { type: "indent", text: "Generate a structured shot list (storyboard) for an" },
      { type: "indent", text: "ad creative before any composition code is written." },
      { type: "indent", text: "Output: markdown table of scenes with timing, scene" },
      { type: "indent", text: "type, copy, motion, and transitions." },
      { type: "blank" },
      { type: "indent", text: "Use when: you have a brief and want a plan before" },
      { type: "indent", text: "committing TSX." },
      { type: "blank" },
      { type: "kv", k: "argument-hint", v: '"<brief> [--format=<id>]"' },
      { type: "kv", k: "context", v: "inline" },
      { type: "kv", k: "model", v: "claude-opus-4-7" },
      { type: "kv", k: "paths", v: '"compositions/**, ads.config.ts"' },
      { type: "kv", k: "allowed-tools", v: "Read, Glob, Grep" },
      { type: "delim" },
    ],
  },
  {
    tabLabel: "/video-review",
    filePath: ".claude/skills/video-review/SKILL.md",
    badge: "fork · claude-sonnet-4-6",
    body: [
      { type: "delim" },
      { type: "kv", k: "name", v: "video-review" },
      { type: "kv-block", k: "description" },
      { type: "indent", text: "Adversarial review of a composition: determinism," },
      { type: "indent", text: "safe-area overflow, FitHeadline mismatch, transition" },
      { type: "indent", text: "budget, voiceover sync, ads-checks rules." },
      { type: "blank" },
      { type: "indent", text: "Forks an `ads-reviewer` agent so the review runs in" },
      { type: "indent", text: "a clean context — heavyweight, audit-style." },
      { type: "blank" },
      { type: "kv", k: "argument-hint", v: '"[<composition-id> | all]"' },
      { type: "kv", k: "context", v: "fork" },
      { type: "kv", k: "agent", v: "ads-reviewer" },
      { type: "kv", k: "model", v: "claude-sonnet-4-6" },
      { type: "kv", k: "paths", v: '"compositions/**, packages/ads-framework/**"' },
      { type: "kv", k: "allowed-tools", v: "Read, Glob, Grep, Bash(pnpm ads:check)" },
      { type: "delim" },
    ],
  },
  {
    tabLabel: "/voiceover",
    filePath: ".claude/skills/voiceover/SKILL.md",
    badge: "inline · claude-opus-4-7",
    body: [
      { type: "delim" },
      { type: "kv", k: "name", v: "voiceover" },
      { type: "kv-block", k: "description" },
      { type: "indent", text: "Add AI-generated voiceover (ElevenLabs) to a composition." },
      { type: "indent", text: "One MP3 per scene under <paths.voiceover>/<id>/." },
      { type: "indent", text: "Auto-burned captions via calculateMetadata." },
      { type: "blank" },
      { type: "kv", k: "argument-hint", v: '"<id> [--voice=<id>]"' },
      { type: "kv", k: "context", v: "inline" },
      { type: "kv", k: "model", v: "claude-opus-4-7" },
      { type: "kv", k: "paths", v: '"compositions/**, public/voiceover/**"' },
      { type: "kv", k: "allowed-tools", v: "Read, Edit, Bash(pnpm ads:voiceover)" },
      { type: "delim" },
    ],
  },
  {
    tabLabel: "/ads-copy",
    filePath: ".claude/skills/ads-copy/SKILL.md",
    badge: "inline · claude-opus-4-7",
    body: [
      { type: "delim" },
      { type: "kv", k: "name", v: "ads-copy" },
      { type: "kv-block", k: "description" },
      { type: "indent", text: "LLM-backed ad copy generator. Reads ads.config.ts" },
      { type: "indent", text: "brand.voice + composition props; returns 5 ranked" },
      { type: "indent", text: "headline / sub / CTA variants ready to paste." },
      { type: "blank" },
      { type: "kv", k: "argument-hint", v: '"<product> [<composition-id>]"' },
      { type: "kv", k: "context", v: "inline" },
      { type: "kv", k: "model", v: "claude-opus-4-7" },
      { type: "kv", k: "paths", v: '"compositions/**, ads.config.ts"' },
      { type: "kv", k: "allowed-tools", v: "Read, Glob" },
      { type: "delim" },
    ],
  },
]

export function SkillPreviewCarousel(): JSX.Element {
  const [active, setActive] = useState(0)
  // Pause auto-advance on hover or any explicit tab click.
  // Passive readers should see the
  // frontmatter VARIETY across skills without having to click; once
  // a buyer engages (hover OR click), the auto-advance stops so we
  // don't yank the tab they're trying to read.
  const [paused, setPaused] = useState(false)
  const pausedRef = useRef(paused)
  // eslint-disable-next-line react-hooks/refs -- intentional state->ref sync, read only by the auto-advance interval (an effect), never to derive render output
  pausedRef.current = paused

  useEffect(() => {
    // SSR-safe: only runs after mount. Honor prefers-reduced-motion
    // by skipping the auto-advance entirely for users who've opted
    // out of motion at the OS level.
    if (typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return
    }
    const id = setInterval(() => {
      if (pausedRef.current) return
      setActive((i) => (i + 1) % SKILLS.length)
    }, 6000)
    return () => clearInterval(id)
  }, [])

  const handleTabClick = useCallback((i: number) => {
    setActive(i)
    setPaused(true)
  }, [])

  const skillIndex = active % SKILLS.length
  let skill = SKILLS[skillIndex]
  const usedFirstSkillFallback = skill === undefined
  if (skill === undefined) {
    if (process.env.NODE_ENV !== "production") {
      throw new Error(
        `SkillPreviewCarousel: no skill at index ${skillIndex} (active=${active}, length=${SKILLS.length})`,
      )
    }
    skill = SKILLS[0]
    if (skill === undefined) {
      throw new Error("SkillPreviewCarousel: SKILLS must not be empty")
    }
  }

  // Warn about an out-of-range index from an effect, not during render — the
  // fallback above already kept the UI working, and clientLogger.warn POSTs to
  // /api/log (a render-path call would re-fire on every re-render).
  useEffect(() => {
    if (usedFirstSkillFallback) {
      clientLogger.warn(
        `SkillPreviewCarousel: invalid active index ${active}; falling back to first skill`,
        { component: "SkillPreviewCarousel" },
      )
    }
  }, [usedFirstSkillFallback, active])

  return (
    <div
      className="overflow-hidden rounded-xl border border-border bg-card shadow-xl"
      onMouseEnter={() => setPaused(true)}
      onFocus={() => setPaused(true)}
    >
      {/* Tab strip — clickable to swap the body; also pauses the
          auto-advance on the first explicit interaction. */}
      <div className="flex items-center gap-1 border-b border-border bg-muted/20 px-2 py-1.5">
        {SKILLS.map((s, i) => {
          const isActive = i === active
          return (
            <button
              key={s.tabLabel}
              type="button"
              onClick={() => handleTabClick(i)}
              className={
                "rounded-md px-2.5 py-1 font-mono text-[11px] font-semibold transition-colors " +
                (isActive
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:bg-background/60 hover:text-foreground")
              }
              aria-pressed={isActive}
              aria-label={`Show ${s.tabLabel} SKILL.md`}
            >
              {s.tabLabel}
            </button>
          )
        })}
      </div>
      {/* Terminal-window chrome + body */}
      <div className="flex items-center gap-3 border-b border-border bg-muted/40 px-4 py-2.5">
        <div className="flex items-center gap-1.5">
          <span className="size-3 rounded-full bg-red-400/80" />
          <span className="size-3 rounded-full bg-yellow-400/80" />
          <span className="size-3 rounded-full bg-green-400/80" />
        </div>
        <span className="font-mono text-[11px] text-muted-foreground">{skill.filePath}</span>
        <span className="ml-auto rounded-full bg-primary/15 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-primary">
          {skill.badge}
        </span>
      </div>
      <pre className="overflow-x-auto px-5 py-5 font-mono text-[12px] leading-[1.65]">
        {skill.body.map((line, i) => (
          <SyntaxLine key={i} line={line} />
        ))}
      </pre>
    </div>
  )
}

function SyntaxLine({ line }: { line: SkillLine }): JSX.Element {
  if (line.type === "delim") return <span className="text-foreground/50">{"---\n"}</span>
  if (line.type === "blank") return <span>{"\n"}</span>
  if (line.type === "indent")
    return <span className="pl-4 text-foreground/75">{line.text + "\n"}</span>
  if (line.type === "kv-block")
    return (
      <span>
        <span className="text-sky-400">{line.k}</span>
        <span className="text-foreground/50">: </span>
        <span className="text-amber-300/90">|</span>
        {"\n"}
      </span>
    )
  return (
    <span>
      <span className="text-sky-400">{line.k}</span>
      <span className="text-foreground/50">: </span>
      <span className="text-amber-300/90">{line.v}</span>
      {"\n"}
    </span>
  )
}
